// src/features/task/services/taskConverters.ts
// Firestore <-> Task 轉換工具（純函數，無副作用）
import {
  Timestamp,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  FieldValue,
  deleteField,
} from 'firebase/firestore';
import { Task } from '../../../types/task';

// Firestore 集合名稱
export const TASKS_COLLECTION = 'tasks';

// 用於 Firestore 更新操作的類型（支持 FieldValue）
export type FirestoreUpdateData = {
  [key: string]: unknown | FieldValue;
};

// 將 Firestore 文檔轉換為 Task 物件
export const convertFirestoreTaskToTask = (doc: QueryDocumentSnapshot | DocumentSnapshot): Task => {
  const data = doc.data();
  if (!data) {
    throw new Error('無法轉換文檔：沒有數據');
  }

  // 處理日期轉換
  const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now());
  const closedAt = data.closedAt?.toDate ? data.closedAt.toDate() : data.closedAt ? new Date(data.closedAt) : undefined;
  const updatedAt = data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined;

  // 轉換 null 為 undefined（以符合 TypeScript 定義）
  return {
    id: doc.id,
    title: data.title,
    status: data.status || 'OPEN',
    createdAt,
    updatedAt,
    closedAt,
    targetDate: data.targetDate || '',
    plan: data.plan || undefined,
    notes: data.notes || undefined,
    category: data.category || data.theme || undefined,
    priority: data.priority || undefined,
    energyLevel: data.energyLevel || undefined,
    estimatedTime: data.estimatedTime || undefined,
    migrationCount: data.migrationCount || 0,
    location: data.location || undefined,
    relatedTaskID: data.relatedTaskID || undefined,
    relatedTaskTitle: data.relatedTaskTitle || undefined,
    originCaptureId: data.originCaptureId || undefined,
    userId: data.userId,
    tags: data.tags || [],
    isQuickTask: data.isQuickTask === true,
    closeReason: data.closeReason,
    scheduledTimeBoxIds: data.scheduledTimeBoxIds || [],
    lastTimeboxedDate: data.lastTimeboxedDate || undefined,
    objectiveId: data.objectiveId || undefined,
  };
};

// 將 Task 資料準備為 Firestore 更新格式
export const prepareTaskForFirestore = (task: Partial<Task>): FirestoreUpdateData => {
  const preparedTask: FirestoreUpdateData = {};

  // 只複製非 undefined 的屬性；null 語義為「明確清空」，轉換為 deleteField()
  Object.entries(task).forEach(([key, value]) => {
    if (value !== undefined) {
      preparedTask[key] = value === null ? deleteField() : value;
    }
  });

  // 特殊處理日期字段
  if (task.createdAt) {
    preparedTask.createdAt = Timestamp.fromDate(new Date(task.createdAt));
  }

  if (task.closedAt) {
    preparedTask.closedAt = Timestamp.fromDate(new Date(task.closedAt));
  }

  return preparedTask;
};

// 輔助函數：檢查任務是否有變更
export function hasTaskChanged(cached: Task, fresh: Task): boolean {
  return (
    cached.status !== fresh.status ||
    cached.title !== fresh.title ||
    (cached.updatedAt?.getTime() || 0) !== (fresh.updatedAt?.getTime() || 0)
  );
}
